/**
 * Web Terminal Component
 * 
 * Browser-based SSH terminal using xterm.js and Socket.IO.
 * Provides seamless SSH access to EC2 instances without downloading keys.
 */

import { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { io, Socket } from 'socket.io-client';
import { Loader2, Terminal as TerminalIcon, XCircle, Maximize2, Minimize2 } from 'lucide-react';
import clsx from 'clsx';

import '@xterm/xterm/css/xterm.css';

interface WebTerminalProps {
  jobId: string;
  onClose?: () => void;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export default function WebTerminal({ jobId, onClose }: WebTerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  const socket = useRef<Socket | null>(null);
  const sessionId = useRef<string | null>(null);
  const isConnecting = useRef(false);
  const isMounted = useRef(true);

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (fitAddon.current) {
        fitAddon.current.fit();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize terminal and connect on mount
  useEffect(() => {
    isMounted.current = true;
    
    // Prevent double-mount in React StrictMode
    const alreadyConnected = socket.current?.connected;
    if (alreadyConnected) {
      console.log('WebTerminal: Socket already connected, skipping initialization');
      return;
    }

    console.log('WebTerminal: Initializing terminal for job', jobId);

    // Initialize terminal
    if (terminalRef.current && !terminalInstance.current) {
      const terminal = new Terminal({
        cursorBlink: true,
        cursorStyle: 'block',
        fontSize: 14,
        fontFamily: 'JetBrains Mono, Menlo, Monaco, Consolas, monospace',
        theme: {
          background: '#0a0a0a',
          foreground: '#e4e4e7',
          cursor: '#10b981',
          cursorAccent: '#0a0a0a',
          selectionBackground: '#10b98140',
          black: '#18181b',
          red: '#ef4444',
          green: '#10b981',
          yellow: '#f59e0b',
          blue: '#3b82f6',
          magenta: '#8b5cf6',
          cyan: '#06b6d4',
          white: '#e4e4e7',
          brightBlack: '#52525b',
          brightRed: '#f87171',
          brightGreen: '#34d399',
          brightYellow: '#fbbf24',
          brightBlue: '#60a5fa',
          brightMagenta: '#a78bfa',
          brightCyan: '#22d3ee',
          brightWhite: '#fafafa',
        },
        allowProposedApi: true,
      });

      const fit = new FitAddon();
      terminal.loadAddon(fit);

      terminal.open(terminalRef.current);
      fit.fit();

      terminalInstance.current = terminal;
      fitAddon.current = fit;

      // Handle terminal input
      terminal.onData((data) => {
        if (socket.current?.connected && sessionId.current) {
          socket.current.emit('terminal:input', {
            sessionId: sessionId.current,
            data,
          });
        }
      });

      // Handle terminal resize
      terminal.onResize(({ cols, rows }) => {
        if (socket.current?.connected && sessionId.current) {
          socket.current.emit('terminal:resize', {
            sessionId: sessionId.current,
            cols,
            rows,
          });
        }
      });
    }

    // Connect to WebSocket
    if (!socket.current && !isConnecting.current) {
      isConnecting.current = true;
      setConnectionState('connecting');
      setErrorMessage('');

      // Handle empty string for relative paths (when behind nginx proxy)
      // Same logic as api.ts - if VITE_API_URL is explicitly set to empty string, use it
      // Otherwise, use the default localhost URL for development
      const API_URL = import.meta.env.VITE_API_URL !== undefined 
        ? import.meta.env.VITE_API_URL 
        : 'http://localhost:3001';
      
      const newSocket = io(`${API_URL}/terminal`, {
        transports: ['websocket'],
        reconnection: false, // We'll handle reconnection manually
        timeout: 30000,
      });

      socket.current = newSocket;

      newSocket.on('connect', () => {
        if (!isMounted.current) return;
        console.log('WebSocket connected, requesting terminal session...');
        // Request terminal connection
        newSocket.emit('terminal:connect', { jobId });
      });

      newSocket.on('terminal:connected', (data: { sessionId: string; message: string }) => {
        if (!isMounted.current) return;
        console.log('Terminal session established:', data.sessionId);
        sessionId.current = data.sessionId;
        isConnecting.current = false;
        setConnectionState('connected');
        
        // Focus terminal
        terminalInstance.current?.focus();
        
        // Send initial resize
        if (terminalInstance.current && fitAddon.current) {
          const dims = fitAddon.current.proposeDimensions();
          if (dims) {
            newSocket.emit('terminal:resize', {
              sessionId: data.sessionId,
              cols: dims.cols,
              rows: dims.rows,
            });
          }
        }
      });

      newSocket.on('terminal:data', (data: { data: string }) => {
        if (!isMounted.current) return;
        terminalInstance.current?.write(data.data);
      });

      newSocket.on('terminal:error', (data: { message: string }) => {
        if (!isMounted.current) return;
        console.error('Terminal error:', data.message);
        isConnecting.current = false;
        setConnectionState('error');
        setErrorMessage(data.message);
        terminalInstance.current?.write(`\r\n\x1b[31mError: ${data.message}\x1b[0m\r\n`);
      });

      newSocket.on('terminal:close', (data: { reason: string }) => {
        if (!isMounted.current) return;
        console.log('Terminal session closed:', data.reason);
        sessionId.current = null;
        setConnectionState('disconnected');
        terminalInstance.current?.write(`\r\n\x1b[33mSession ended: ${data.reason}\x1b[0m\r\n`);
      });

      newSocket.on('disconnect', (reason) => {
        if (!isMounted.current) return;
        console.log('WebSocket disconnected:', reason);
        sessionId.current = null;
        isConnecting.current = false;
        setConnectionState('disconnected');
      });

      newSocket.on('connect_error', (error) => {
        if (!isMounted.current) return;
        console.error('Connection error:', error);
        isConnecting.current = false;
        setConnectionState('error');
        setErrorMessage('Failed to connect to server');
      });
    }

    // Cleanup on unmount
    return () => {
      isMounted.current = false;
      
      if (socket.current) {
        if (sessionId.current) {
          socket.current.emit('terminal:disconnect', { sessionId: sessionId.current });
        }
        socket.current.disconnect();
        socket.current = null;
      }
      
      sessionId.current = null;
      isConnecting.current = false;
      
      if (terminalInstance.current) {
        terminalInstance.current.dispose();
        terminalInstance.current = null;
      }
    };
  }, [jobId]); // Only depend on jobId

  // Refit when fullscreen changes
  useEffect(() => {
    setTimeout(() => {
      fitAddon.current?.fit();
    }, 100);
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleRetry = () => {
    // Cleanup existing connection
    if (socket.current) {
      socket.current.disconnect();
      socket.current = null;
    }
    sessionId.current = null;
    isConnecting.current = false;
    
    // Clear terminal
    terminalInstance.current?.clear();
    
    // Trigger reconnection by resetting state
    setConnectionState('connecting');
    setErrorMessage('');

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    
    const newSocket = io(`${API_URL}/terminal`, {
      transports: ['websocket'],
      reconnection: false,
      timeout: 30000,
    });

    socket.current = newSocket;
    isConnecting.current = true;

    newSocket.on('connect', () => {
      console.log('Retry: WebSocket connected');
      newSocket.emit('terminal:connect', { jobId });
    });

    newSocket.on('terminal:connected', (data: { sessionId: string }) => {
      sessionId.current = data.sessionId;
      isConnecting.current = false;
      setConnectionState('connected');
      terminalInstance.current?.focus();
    });

    newSocket.on('terminal:data', (data: { data: string }) => {
      terminalInstance.current?.write(data.data);
    });

    newSocket.on('terminal:error', (data: { message: string }) => {
      isConnecting.current = false;
      setConnectionState('error');
      setErrorMessage(data.message);
    });

    newSocket.on('terminal:close', (data: { reason: string }) => {
      sessionId.current = null;
      setConnectionState('disconnected');
      terminalInstance.current?.write(`\r\n\x1b[33m${data.reason}\x1b[0m\r\n`);
    });

    newSocket.on('disconnect', () => {
      isConnecting.current = false;
      setConnectionState('disconnected');
    });

    newSocket.on('connect_error', () => {
      isConnecting.current = false;
      setConnectionState('error');
      setErrorMessage('Failed to connect');
    });
  };

  return (
    <div
      className={clsx(
        'flex flex-col bg-surface-900 border border-surface-700 rounded-lg overflow-hidden',
        isFullscreen && 'fixed inset-4 z-50'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-surface-800 border-b border-surface-700">
        <div className="flex items-center gap-2">
          <TerminalIcon className="w-4 h-4 text-gpu-400" />
          <span className="text-sm font-medium text-surface-200">SSH Terminal</span>
          <span
            className={clsx(
              'text-xs px-2 py-0.5 rounded-full',
              connectionState === 'connected' && 'bg-green-500/20 text-green-400',
              connectionState === 'connecting' && 'bg-yellow-500/20 text-yellow-400',
              connectionState === 'disconnected' && 'bg-surface-600 text-surface-400',
              connectionState === 'error' && 'bg-red-500/20 text-red-400'
            )}
          >
            {connectionState}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-1 hover:bg-surface-700 rounded transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4 text-surface-400" />
            ) : (
              <Maximize2 className="w-4 h-4 text-surface-400" />
            )}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-surface-700 rounded transition-colors"
              title="Close terminal"
            >
              <XCircle className="w-4 h-4 text-surface-400" />
            </button>
          )}
        </div>
      </div>

      {/* Terminal */}
      <div className={clsx('relative flex-1', isFullscreen ? 'h-full' : 'h-80')}>
        {connectionState === 'connecting' && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-900/80 z-10">
            <div className="flex items-center gap-3 text-surface-300">
              <Loader2 className="w-5 h-5 animate-spin text-gpu-400" />
              <span>Connecting to instance...</span>
            </div>
          </div>
        )}
        
        {connectionState === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-900/80 z-10">
            <div className="text-center">
              <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-400 mb-2">{errorMessage}</p>
              <button
                onClick={handleRetry}
                className="btn-secondary text-sm"
              >
                Retry Connection
              </button>
            </div>
          </div>
        )}

        <div
          ref={terminalRef}
          className="w-full h-full p-2"
          style={{ backgroundColor: '#0a0a0a' }}
        />
      </div>
    </div>
  );
}
